import React from 'react';
import { connect } from 'react-redux';
import { Card } from '@drone-js/core';
import OfferCardFooter from 'component/page/detail/PackageOptions/CollapsedOffer/OfferCardBottomSection/OfferCardFooter';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesAvailableOptionCardClickEventActivitiesFieldsV6 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesAvailableOptionCardClickEventActivitiesManifestV6 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { mapDispatchToProps, mapStateToProps, mergeProps, Props } from 'component/page/detail/PackageOptions/CollapsedOffer/OfferCard/OfferCard.selectors';
import OfferGroupCardDetail from 'component/page/detail/PackageOptions/CollapsedOffer/OfferCardDetail/OfferGroupCardDetail';
import { getOfferGroupDetailByRef } from 'component/page/detail/detailUtils';
import { getDateInYmdFormat } from 'core-framework/util/datetime/date';
import { getOfferWithMinPricingSummary, mapTotalPriceByOffer } from 'gql/service/graph/activityService';
import { roundNumberWithDecimal } from 'core/utils/roundNumberWithDecimal';
import DebugInformation from 'component/common/ActivityCard/DebugInformation/DebugInformation';
import { usePackageOptionsModularContext } from 'component/page/detail/PackageOptions/PackageOptionsModularContext';

const OfferCard: React.FC<Props> = (props) => {
    const {
        offer,
        config,
        index,
        supplier,
        isUnavailableOffer,
        selectedOfferId,
        selectedDate,
        getCalendarPrice,
        setNotification,
        isLoading,
        detail,
        sendAvailabilityOffersMeasurement,
        offerLength,
    } = props;
    const { isExpandOfferCard } = usePackageOptionsModularContext();
    const pageTypeId = config.pageConfig.pageTypeId;
    const navigationId = `available-option-card-${index}`;
    const isSelectedOffer = offer.offerGroupId === selectedOfferId;
    const offerGroupDetail = getOfferGroupDetailByRef(detail, offer.offerGroupDetailRef);
    const offerRefCondition = offer?.offerGroupDetailRef;
    const offerDetailRefCondition = offerGroupDetail?.offerGroupDetailsReference;
    const selectedFirstOffer = isExpandOfferCard && offerLength === 1;
    // We support BE to return only 1 pricing and 1 offer at availability endpoint level\

    const [isDisplayWarning, setIsDisplayWarning] = React.useState(false);
    const [expandOffer, setExpandOffer] = React.useState(false);

    React.useEffect(() => {
        if (isSelectedOffer) {
            setNotification();
            setIsDisplayWarning(false);
        }
        props.isClickCardExpandOffer && setExpandOffer(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSelectedOffer, index, setNotification]);

    React.useEffect(() => {
        if (!isLoading) {
            sendAvailabilityOffersMeasurement(pageTypeId, offerDetailRefCondition, offerRefCondition, offer.activityId, offer.offerId, offer.supplierId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading]);

    const onCardClick = () => {
        // Only send tracking and expand offer if the offer is not already selected/expanded
        if (!isSelectedOffer) {
            const enrichedOfferOption = offer.enrichedOfferOption || [];
            const pricingSummary = getOfferWithMinPricingSummary(enrichedOfferOption)?.pricingSummary;
            const offerPrice = mapTotalPriceByOffer(pricingSummary);
            const calendarPrice = getCalendarPrice(enrichedOfferOption);
            trackElementEvent<ActivitiesAvailableOptionCardClickEventActivitiesFieldsV6>(
                ActivitiesAvailableOptionCardClickEventActivitiesManifestV6.ids,
                {
                    activities_is_unavailable_offer: isUnavailableOffer || false,
                    travel_date: getDateInYmdFormat(selectedDate),
                    final_price: offerPrice,
                    // Need to round due to IEEE 754 floating point arithmetic
                    price_difference: roundNumberWithDecimal(offerPrice - calendarPrice, 2),
                },
                { action_element_index: index + 1, offer_id: offer.offerId, activity_id: offer.activityId },
            );
            props.isClickCardExpandOffer && setExpandOffer(true);
        }
    };

    if (!offerGroupDetail || !offerGroupDetail.offerGroupTitle) return null;

    return (
        <>
            <DebugInformation
                supplierId={supplier}
                supplierActivityCode={offer.supplierActivityCode}
                activityId={offer.activityId}
                offerId={offer.offerId}
                supplierOfferCode={offer.supplierOfferCode}
            />
            {/* eslint-disable-next-line @agoda/agoda-a11y/no-incorrect-props -- @agoda/agoda-a11y/no-incorrect-props[b1a0cfca-c77f-49e6-b917-75a8ba27e482] */}
            <Card
                id={navigationId}
                borderRadius="8"
                my="12"
                p="16"
                data-element-name="activities-available-option-card"
                data-testid="activities-available-option-card"
                data-element-index={index + 1}
                onClick={onCardClick}
                type="interactive"
                status={isSelectedOffer ? 'selected' : 'default'}
            >
                <OfferGroupCardDetail
                    offer={offer}
                    offerIndex={index}
                    navigationId={navigationId}
                    index={index}
                    isSelectedOffer={isSelectedOffer}
                    setIsDisplayWarning={setIsDisplayWarning}
                    isDisplayWarning={isDisplayWarning}
                    isLoading={isLoading}
                    offerGroupDetail={offerGroupDetail}
                />
                <OfferCardFooter
                    offer={offer}
                    isUnavailableOffer={isUnavailableOffer}
                    offerIndex={index}
                    supplier={supplier}
                    navigationId={navigationId}
                    isSelectedOffer={isSelectedOffer}
                    setIsDisplayWarning={setIsDisplayWarning}
                    isDisplayWarning={isDisplayWarning}
                    shouldExpandOffer={expandOffer}
                    selectedFirstOffer={selectedFirstOffer}
                />
            </Card>
        </>
    );
};
OfferCard.displayName = 'OfferCard';

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(OfferCard);
